/**
 * Vanilla port of LiveKit's `BarVisualizer` (+ `useBarAnimator` /
 * `useMultibandTrackVolume`): frequency-band bar heights from an AnalyserNode
 * plus the state-driven highlight sequencer. Emits the exact same DOM —
 * `.lk-audio-bar-visualizer` / `.lk-audio-bar` / `.lk-highlighted` /
 * `data-lk-va-state` — so the shared lime override CSS applies verbatim.
 *
 * The analyser comes from a provider callback (the SDK caches per track and
 * returns null until the track exists), re-read on every sample tick so bars
 * light up the moment the mic/dealer track appears mid-hold.
 */

export type VisualizerState =
  | 'speaking'
  | 'listening'
  | 'thinking'
  | 'connecting'
  | 'initializing'
  | 'idle'
  | 'disconnected';

export type AnalyserProvider = () => AnalyserNode | null;

export interface BarVisualizerOptions {
  barCount?: number;
  /** Bar height floor/ceiling in percent (LiveKit options.minHeight/maxHeight). */
  minHeight?: number;
  maxHeight?: number;
}

export interface BarVisualizerHandle {
  readonly element: HTMLElement;
  /**
   * Set the sequencer state and (re)start the render loop. `null` stops the
   * loop and zeroes the bars — call when the visualizer is hidden.
   */
  update(state: VisualizerState | null, analyserProvider?: AnalyserProvider): void;
  destroy(): void;
}

/** How often bar heights re-sample the analyser (useMultibandTrackVolume default). */
const BAND_UPDATE_INTERVAL_MS = 32;
/** Frequency-bin window (BarVisualizer passes loPass 100 / hiPass 200). */
const LO_PASS = 100;
const HI_PASS = 200;

/** Ping-pong sweep used for connecting/initializing (connectingSequence.ts). */
export function generateConnectingSequence(columns: number): number[][] {
  const seq: number[][] = [];
  for (let x = 0; x < columns; x++) {
    seq.push([x, columns - 1 - x]);
  }
  return seq;
}

/** Center-bar pulse used for listening/thinking (listeningSequence.ts). */
export function generateListeningSequence(columns: number): number[][] {
  const center = Math.floor(columns / 2);
  return [[center], [-1]];
}

/** Highlight frames + advance interval per state (BarVisualizer sequencer tables). */
export function sequenceFor(
  state: VisualizerState,
  columns: number
): { frames: number[][]; intervalMs: number } {
  switch (state) {
    case 'speaking':
      return { frames: [Array.from({ length: columns }, (_, i) => i)], intervalMs: 100 };
    case 'listening':
      return { frames: generateListeningSequence(columns), intervalMs: 500 };
    case 'thinking':
      return { frames: generateListeningSequence(columns), intervalMs: 150 };
    case 'connecting':
    case 'initializing':
      return { frames: generateConnectingSequence(columns), intervalMs: 2000 / columns };
    default:
      // idle / disconnected: no highlights (all bars stay dim at min height)
      return { frames: [[]], intervalMs: 1000 };
  }
}

/**
 * Fold raw `getFloatFrequencyData` output into per-bar volumes in [0, 1]:
 * slice the loPass..hiPass bin window, normalize dB [-100, -10] with a sqrt
 * curve, then average proportional chunks (useMultibandTrackVolume port).
 */
export function computeBands(
  frequencyData: Float32Array,
  bands: number,
  loPass: number = LO_PASS,
  hiPass: number = HI_PASS
): number[] {
  const windowed = frequencyData.slice(loPass, hiPass);
  const normalized = new Array<number>(windowed.length);
  for (let i = 0; i < windowed.length; i++) {
    const value = windowed[i];
    if (value === -Infinity) {
      normalized[i] = 0;
    } else {
      const db = 1 - (Math.max(-100, Math.min(-10, value)) * -1) / 100;
      normalized[i] = Math.sqrt(db);
    }
  }
  const chunks: number[] = [];
  for (let i = 0; i < bands; i++) {
    const start = Math.floor((i * normalized.length) / bands);
    const end = Math.floor(((i + 1) * normalized.length) / bands);
    let sum = 0;
    for (let j = start; j < end; j++) sum += normalized[j];
    chunks.push(end > start ? sum / (end - start) : 0);
  }
  return chunks;
}

export function createBarVisualizer(options: BarVisualizerOptions = {}): BarVisualizerHandle {
  const barCount = options.barCount ?? 24;
  const minHeight = options.minHeight ?? 20;
  const maxHeight = options.maxHeight ?? 100;

  const element = document.createElement('div');
  element.className = 'lk-audio-bar-visualizer';
  const bars: HTMLSpanElement[] = [];
  for (let i = 0; i < barCount; i++) {
    const bar = document.createElement('span');
    bar.className = 'lk-audio-bar';
    bar.dataset.lkBarIndex = String(i);
    element.appendChild(bar);
    bars.push(bar);
  }

  let state: VisualizerState | null = null;
  let provider: AnalyserProvider = () => null;
  let frames: number[][] = [[]];
  let intervalMs = 1000;
  let frameIndex = 0;
  let lastSequenceAt = 0;
  let lastBandsAt = 0;
  let rafId: number | null = null;
  let frequencyData: Float32Array | null = null;

  const applyHeights = (volumes: number[]): void => {
    for (let i = 0; i < bars.length; i++) {
      const volume = volumes[i] ?? 0;
      bars[i].style.height = `${Math.min(maxHeight, Math.max(minHeight, volume * 100 + 5))}%`;
    }
  };

  const applyHighlights = (): void => {
    const highlighted = frames[frameIndex % frames.length] ?? [];
    for (let i = 0; i < bars.length; i++) {
      const on = highlighted.includes(i);
      bars[i].classList.toggle('lk-highlighted', on);
      bars[i].dataset.lkHighlighted = String(on);
    }
  };

  const sampleBands = (): void => {
    const analyser = provider();
    if (!analyser) {
      applyHeights([]);
      return;
    }
    if (!frequencyData || frequencyData.length !== analyser.frequencyBinCount) {
      frequencyData = new Float32Array(analyser.frequencyBinCount);
    }
    analyser.getFloatFrequencyData(frequencyData);
    applyHeights(computeBands(frequencyData, barCount));
  };

  const tick = (time: number): void => {
    if (time - lastBandsAt >= BAND_UPDATE_INTERVAL_MS) {
      sampleBands();
      lastBandsAt = time;
    }
    if (time - lastSequenceAt >= intervalMs) {
      frameIndex++;
      applyHighlights();
      lastSequenceAt = time;
    }
    rafId = requestAnimationFrame(tick);
  };

  const stop = (): void => {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  };

  return {
    element,
    update(nextState, analyserProvider) {
      provider = analyserProvider ?? provider;
      if (nextState !== state) {
        state = nextState;
        if (state === null) {
          element.removeAttribute('data-lk-va-state');
          frames = [[]];
        } else {
          element.setAttribute('data-lk-va-state', state);
          ({ frames, intervalMs } = sequenceFor(state, barCount));
        }
        frameIndex = 0;
        lastSequenceAt = 0;
        applyHighlights();
      }
      if (state === null) {
        stop();
        applyHeights([]);
        return;
      }
      if (rafId === null) {
        lastBandsAt = 0;
        rafId = requestAnimationFrame(tick);
      }
    },
    destroy() {
      stop();
      element.remove();
    },
  };
}
